import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  DocumentReference,
  WriteBatch,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Project, Moment, Asset, MaterialLineItem } from '../types';

export class ProjectService {
  /**
   * Create a new project
   */
  static async createProject(
    project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<Project> {
    try {
      const projectRef = doc(collection(db, 'projects'));
      const now = new Date();

      // Use provided expiration date or default to 30 days from now
      let expirationDate = project.expirationDate;
      if (!expirationDate) {
        expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 30);
      }

      const projectData: any = {
        ...project,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        expirationDate: Timestamp.fromDate(expirationDate),
        eventDate: project.eventDate ? Timestamp.fromDate(project.eventDate) : null,
        installStartDate: project.installStartDate ? Timestamp.fromDate(project.installStartDate) : null,
        installEndDate: project.installEndDate ? Timestamp.fromDate(project.installEndDate) : null,
        revisionDeadline: project.revisionDeadline ? Timestamp.fromDate(project.revisionDeadline) : null,
        moments: project.moments || [], // Use provided moments or empty array
      };

      await setDoc(projectRef, projectData);

      return {
        ...project,
        id: projectRef.id,
        moments: project.moments || [],
        createdAt: now,
        updatedAt: now,
        expirationDate: expirationDate,
      };
    } catch (error: any) {
      throw new Error(`Failed to create project: ${error.message}`);
    }
  }

  /**
   * Get a single project by ID
   */
  static async getProject(id: string): Promise<Project | null> {
    try {
      const docSnap = await getDoc(doc(db, 'projects', id));
      if (docSnap.exists()) {
        const data = docSnap.data();

        // Helper function to convert any date format to Date object
        const convertToDate = (dateValue: any): Date => {
          if (!dateValue) return new Date();
          if (typeof dateValue.toMillis === 'function') {
            return new Date(dateValue.toMillis());
          } else if (dateValue instanceof Date) {
            return dateValue;
          } else if (typeof dateValue === 'string') {
            return new Date(dateValue);
          }
          return new Date();
        };

        // Helper function to convert optional date field
        const convertOptionalDate = (dateValue: any): Date | undefined => {
          if (!dateValue) return undefined;
          if (typeof dateValue.toMillis === 'function') {
            return new Date(dateValue.toMillis());
          } else if (dateValue instanceof Date) {
            return dateValue;
          } else if (typeof dateValue === 'string') {
            return new Date(dateValue);
          }
          return undefined;
        };

        const expirationDate = convertOptionalDate(data.expirationDate);
        const isExpired = expirationDate ? new Date() > expirationDate : false;

        return {
          ...data,
          id: docSnap.id,
          createdAt: convertToDate(data.createdAt),
          updatedAt: convertToDate(data.updatedAt),
          eventDate: convertOptionalDate(data.eventDate),
          installStartDate: convertOptionalDate(data.installStartDate),
          installEndDate: convertOptionalDate(data.installEndDate),
          revisionDeadline: convertOptionalDate(data.revisionDeadline),
          expirationDate: expirationDate,
          isExpired,
          moments: data.moments || [],
        } as Project;
      }
      return null;
    } catch (error: any) {
      throw new Error(`Failed to get project: ${error.message}`);
    }
  }

  /**
   * Get all projects (visible to all users)
   */
  static async getAllProjects(): Promise<Project[]> {
    try {
      const q = query(
        collection(db, 'projects'),
        orderBy('updatedAt', 'desc')
      );
      const querySnap = await getDocs(q);
      return querySnap.docs.map((doc) => {
        const data = doc.data();

        // Helper function to convert any date format to Date object
        const convertToDate = (dateValue: any): Date => {
          if (!dateValue) return new Date();
          if (typeof dateValue.toMillis === 'function') {
            return new Date(dateValue.toMillis());
          } else if (dateValue instanceof Date) {
            return dateValue;
          } else if (typeof dateValue === 'string') {
            return new Date(dateValue);
          }
          return new Date();
        };

        // Helper function to convert optional date field
        const convertOptionalDate = (dateValue: any): Date | undefined => {
          if (!dateValue) return undefined;
          if (typeof dateValue.toMillis === 'function') {
            return new Date(dateValue.toMillis());
          } else if (dateValue instanceof Date) {
            return dateValue;
          } else if (typeof dateValue === 'string') {
            return new Date(dateValue);
          }
          return undefined;
        };

        const expirationDate = convertOptionalDate(data.expirationDate);
        const isExpired = expirationDate ? new Date() > expirationDate : false;

        return {
          ...data,
          id: doc.id,
          createdAt: convertToDate(data.createdAt),
          updatedAt: convertToDate(data.updatedAt),
          eventDate: convertOptionalDate(data.eventDate),
          installStartDate: convertOptionalDate(data.installStartDate),
          installEndDate: convertOptionalDate(data.installEndDate),
          revisionDeadline: convertOptionalDate(data.revisionDeadline),
          expirationDate: expirationDate,
          isExpired,
          moments: data.moments || [],
        } as Project;
      });
    } catch (error: any) {
      throw new Error(`Failed to get projects: ${error.message}`);
    }
  }

  /**
   * Get all projects for a user
   */
  static async getProjectsByUser(userId: string): Promise<Project[]> {
    try {
      const q = query(
        collection(db, 'projects'),
        where('createdBy', '==', userId),
        orderBy('updatedAt', 'desc')
      );
      const querySnap = await getDocs(q);
      return querySnap.docs.map((doc) => {
        const data = doc.data();

        // Helper function to convert any date format to Date object
        const convertToDate = (dateValue: any): Date => {
          if (!dateValue) return new Date();
          if (typeof dateValue.toMillis === 'function') {
            return new Date(dateValue.toMillis());
          } else if (dateValue instanceof Date) {
            return dateValue;
          } else if (typeof dateValue === 'string') {
            return new Date(dateValue);
          }
          return new Date();
        };

        // Helper function to convert optional date field
        const convertOptionalDate = (dateValue: any): Date | undefined => {
          if (!dateValue) return undefined;
          if (typeof dateValue.toMillis === 'function') {
            return new Date(dateValue.toMillis());
          } else if (dateValue instanceof Date) {
            return dateValue;
          } else if (typeof dateValue === 'string') {
            return new Date(dateValue);
          }
          return undefined;
        };

        const expirationDate = convertOptionalDate(data.expirationDate);
        const isExpired = expirationDate ? new Date() > expirationDate : false;

        return {
          ...data,
          id: doc.id,
          createdAt: convertToDate(data.createdAt),
          updatedAt: convertToDate(data.updatedAt),
          eventDate: convertOptionalDate(data.eventDate),
          installStartDate: convertOptionalDate(data.installStartDate),
          installEndDate: convertOptionalDate(data.installEndDate),
          revisionDeadline: convertOptionalDate(data.revisionDeadline),
          expirationDate: expirationDate,
          isExpired,
          moments: data.moments || [],
        } as Project;
      });
    } catch (error: any) {
      throw new Error(`Failed to get projects: ${error.message}`);
    }
  }

  /**
   * Recursively clean undefined values from objects and arrays
   * Note: null values are preserved (needed to clear fields in Firestore)
   */
  private static cleanData(obj: any): any {
    if (obj === undefined) return undefined;
    if (obj === null) return null; // Keep null values

    // Preserve Firestore Timestamps (they have a toMillis method)
    if (obj && typeof obj === 'object' && typeof obj.toMillis === 'function') {
      return obj; // Return Timestamp as-is, don't clean it
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.cleanData(item)).filter(item => item !== undefined);
    }

    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        const cleanedValue = this.cleanData(value);
        if (cleanedValue !== undefined) {
          cleaned[key] = cleanedValue; // This will now include null values
        }
      }
      return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    }

    return obj;
  }

  /**
   * Update a project
   */
  static async updateProject(
    id: string,
    updates: Partial<Project>
  ): Promise<void> {
    try {
      // DEBUG: Log what we received
      console.log('=== PROJECT SERVICE UPDATE DEBUG ===');
      console.log('Received updates:', updates);
      console.log('eventDate from updates:', (updates as any).eventDate);
      console.log('installStartDate from updates:', (updates as any).installStartDate);
      console.log('installEndDate from updates:', (updates as any).installEndDate);
      console.log('revisionDeadline from updates:', (updates as any).revisionDeadline);

      const { id: _id, createdAt, updatedAt, expirationDate, eventDate, installStartDate, installEndDate, revisionDeadline, isExpired, ...dataToUpdate } = updates as any;

      // Convert date fields to Timestamp if provided
      const updateData: any = {
        ...dataToUpdate,
        updatedAt: Timestamp.now(),
      };

      // Helper to convert date to Timestamp or null
      const convertDateField = (dateField: Date | null | undefined): any => {
        if (dateField === null || dateField === undefined) {
          return null;
        } else if (dateField instanceof Date && !isNaN(dateField.getTime())) {
          return Timestamp.fromDate(dateField);
        }
        return null;
      };

      if (expirationDate !== undefined) {
        updateData.expirationDate = convertDateField(expirationDate);
        console.log('Setting expirationDate:', updateData.expirationDate);
      }
      if (eventDate !== undefined) {
        updateData.eventDate = convertDateField(eventDate);
        console.log('Setting eventDate:', updateData.eventDate);
      }
      if (installStartDate !== undefined) {
        updateData.installStartDate = convertDateField(installStartDate);
        console.log('Setting installStartDate:', updateData.installStartDate);
      }
      if (installEndDate !== undefined) {
        updateData.installEndDate = convertDateField(installEndDate);
        console.log('Setting installEndDate:', updateData.installEndDate);
      }
      if (revisionDeadline !== undefined) {
        updateData.revisionDeadline = convertDateField(revisionDeadline);
        console.log('Setting revisionDeadline:', updateData.revisionDeadline);
      }

      // Deep clean to remove undefined values recursively (but keep null values)
      const cleanedData = this.cleanData(updateData);

      console.log('Final cleanedData being sent to Firestore:', cleanedData);
      console.log('eventDate in cleanedData:', cleanedData?.eventDate);
      console.log('installStartDate in cleanedData:', cleanedData?.installStartDate);
      console.log('installEndDate in cleanedData:', cleanedData?.installEndDate);

      await updateDoc(doc(db, 'projects', id), cleanedData);
    } catch (error: any) {
      throw new Error(`Failed to update project: ${error.message}`);
    }
  }

  /**
   * Delete a project
   */
  static async deleteProject(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'projects', id));
    } catch (error: any) {
      throw new Error(`Failed to delete project: ${error.message}`);
    }
  }

  /**
   * Add a moment to a project
   */
  static async addMoment(projectId: string, moment: Omit<Moment, 'id'>): Promise<Moment> {
    try {
      const project = await this.getProject(projectId);
      if (!project) throw new Error('Project not found');

      const momentId = doc(collection(db, 'dummy')).id; // Generate ID
      const newMoment: Moment = {
        id: momentId,
        ...moment,
        assets: [],
      };

      const updatedMoments = [...(project.moments || []), newMoment];
      await this.updateProject(projectId, { moments: updatedMoments } as any);

      return newMoment;
    } catch (error: any) {
      throw new Error(`Failed to add moment: ${error.message}`);
    }
  }

  /**
   * Update a moment
   */
  static async updateMoment(
    projectId: string,
    momentId: string,
    updates: Partial<Moment>
  ): Promise<void> {
    try {
      const project = await this.getProject(projectId);
      if (!project) throw new Error('Project not found');

      const updatedMoments = project.moments.map((m) =>
        m.id === momentId ? { ...m, ...updates } : m
      );

      await this.updateProject(projectId, { moments: updatedMoments } as any);
    } catch (error: any) {
      throw new Error(`Failed to update moment: ${error.message}`);
    }
  }

  /**
   * Delete a moment
   */
  static async deleteMoment(projectId: string, momentId: string): Promise<void> {
    try {
      const project = await this.getProject(projectId);
      if (!project) throw new Error('Project not found');

      const updatedMoments = project.moments.filter((m) => m.id !== momentId);
      await this.updateProject(projectId, { moments: updatedMoments } as any);
    } catch (error: any) {
      throw new Error(`Failed to delete moment: ${error.message}`);
    }
  }

  /**
   * Add an asset to a moment
   */
  static async addAsset(
    projectId: string,
    momentId: string,
    asset: Omit<Asset, 'id'>
  ): Promise<Asset> {
    try {
      const project = await this.getProject(projectId);
      if (!project) throw new Error('Project not found');

      const moment = project.moments.find((m) => m.id === momentId);
      if (!moment) throw new Error('Moment not found');

      const assetId = doc(collection(db, 'dummy')).id; // Generate ID
      const newAsset: Asset = {
        id: assetId,
        ...asset,
      };

      const updatedAssets = [...(moment.assets || []), newAsset];
      const updatedMoments = project.moments.map((m) =>
        m.id === momentId ? { ...m, assets: updatedAssets } : m
      );

      await this.updateProject(projectId, { moments: updatedMoments } as any);

      return newAsset;
    } catch (error: any) {
      throw new Error(`Failed to add asset: ${error.message}`);
    }
  }

  /**
   * Update an asset
   */
  static async updateAsset(
    projectId: string,
    momentId: string,
    assetId: string,
    updates: Partial<Asset>
  ): Promise<void> {
    try {
      const project = await this.getProject(projectId);
      if (!project) throw new Error('Project not found');

      const moment = project.moments.find((m) => m.id === momentId);
      if (!moment) throw new Error('Moment not found');

      const updatedAssets = moment.assets.map((a) =>
        a.id === assetId ? { ...a, ...updates } : a
      );

      const updatedMoments = project.moments.map((m) =>
        m.id === momentId ? { ...m, assets: updatedAssets } : m
      );

      await this.updateProject(projectId, { moments: updatedMoments } as any);
    } catch (error: any) {
      throw new Error(`Failed to update asset: ${error.message}`);
    }
  }

  /**
   * Delete an asset
   */
  static async deleteAsset(
    projectId: string,
    momentId: string,
    assetId: string
  ): Promise<void> {
    try {
      const project = await this.getProject(projectId);
      if (!project) throw new Error('Project not found');

      const moment = project.moments.find((m) => m.id === momentId);
      if (!moment) throw new Error('Moment not found');

      const updatedAssets = moment.assets.filter((a) => a.id !== assetId);
      const updatedMoments = project.moments.map((m) =>
        m.id === momentId ? { ...m, assets: updatedAssets } : m
      );

      await this.updateProject(projectId, { moments: updatedMoments } as any);
    } catch (error: any) {
      throw new Error(`Failed to delete asset: ${error.message}`);
    }
  }

  /**
   * Add a material line item to an asset
   */
  static async addMaterialLineItem(
    projectId: string,
    momentId: string,
    assetId: string,
    material: MaterialLineItem
  ): Promise<void> {
    try {
      const project = await this.getProject(projectId);
      if (!project) throw new Error('Project not found');

      const moment = project.moments.find((m) => m.id === momentId);
      if (!moment) throw new Error('Moment not found');

      const asset = moment.assets.find((a) => a.id === assetId);
      if (!asset) throw new Error('Asset not found');

      const updatedMaterials = [...(asset.materials || []), material];
      const updatedAssets = moment.assets.map((a) =>
        a.id === assetId ? { ...a, materials: updatedMaterials } : a
      );

      const updatedMoments = project.moments.map((m) =>
        m.id === momentId ? { ...m, assets: updatedAssets } : m
      );

      await this.updateProject(projectId, { moments: updatedMoments } as any);
    } catch (error: any) {
      throw new Error(`Failed to add material: ${error.message}`);
    }
  }

  /**
   * Update a material line item
   */
  static async updateMaterialLineItem(
    projectId: string,
    momentId: string,
    assetId: string,
    materialId: string,
    updates: Partial<MaterialLineItem>
  ): Promise<void> {
    try {
      const project = await this.getProject(projectId);
      if (!project) throw new Error('Project not found');

      const moment = project.moments.find((m) => m.id === momentId);
      if (!moment) throw new Error('Moment not found');

      const asset = moment.assets.find((a) => a.id === assetId);
      if (!asset) throw new Error('Asset not found');

      const updatedMaterials = asset.materials.map((m) =>
        m.id === materialId ? { ...m, ...updates } : m
      );

      const updatedAssets = moment.assets.map((a) =>
        a.id === assetId ? { ...a, materials: updatedMaterials } : a
      );

      const updatedMoments = project.moments.map((m) =>
        m.id === momentId ? { ...m, assets: updatedAssets } : m
      );

      await this.updateProject(projectId, { moments: updatedMoments } as any);
    } catch (error: any) {
      throw new Error(`Failed to update material: ${error.message}`);
    }
  }

  /**
   * Delete a material line item
   */
  static async deleteMaterialLineItem(
    projectId: string,
    momentId: string,
    assetId: string,
    materialId: string
  ): Promise<void> {
    try {
      const project = await this.getProject(projectId);
      if (!project) throw new Error('Project not found');

      const moment = project.moments.find((m) => m.id === momentId);
      if (!moment) throw new Error('Moment not found');

      const asset = moment.assets.find((a) => a.id === assetId);
      if (!asset) throw new Error('Asset not found');

      const updatedMaterials = asset.materials.filter((m) => m.id !== materialId);
      const updatedAssets = moment.assets.map((a) =>
        a.id === assetId ? { ...a, materials: updatedMaterials } : a
      );

      const updatedMoments = project.moments.map((m) =>
        m.id === momentId ? { ...m, assets: updatedAssets } : m
      );

      await this.updateProject(projectId, { moments: updatedMoments } as any);
    } catch (error: any) {
      throw new Error(`Failed to delete material: ${error.message}`);
    }
  }
}
